import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  colors,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import NavItem from 'src/components/NavItem';
import { getMenu } from 'src/utils/storage/NavBarAdet';

const styles = {
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mobileDrawer: {
    width: 300,
  },
  desktopDrawer: {
    width: 300,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  navigation: (theme) => ({
    overflow: 'auto',
    padding: theme.spacing(1, 2, 2, 2),
    flexGrow: 1
  }),
  menuHeader: (theme) => ({
    padding: theme.spacing(2, 2, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  }),
  menuTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 1.25
  },
  searchField: (theme) => ({
    '& .MuiOutlinedInput-root': {
      height: 42,
      borderRadius: 2,
      backgroundColor: theme.palette.action.hover,
      transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color']),
      '& fieldset': {
        borderColor: 'transparent'
      },
      '&:hover': {
        backgroundColor: theme.palette.action.selected
      },
      '&:hover fieldset': {
        borderColor: theme.palette.divider
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      }
    }
  }),
  emptyState: (theme) => ({
    margin: theme.spacing(3, 1),
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    borderRadius: 2,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px dashed ${theme.palette.divider}`
  }),
  profile: (theme) => ({
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  }),
  badge: (theme) => ({
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
  }),
  badgeDot: {
    height: 9,
    minWidth: 9
  },
  onlineBadge: {
    backgroundColor: colors.green[600]
  },
  awayBadge: {
    backgroundColor: colors.orange[600]
  },
  busyBadge: {
    backgroundColor: colors.red[600]
  },
  offlineBadge: {
    backgroundColor: colors.grey[300]
  },
  avatar: {
    cursor: 'pointer',
    width: 40,
    height: 40
  },
  details: (theme) => ({
    marginLeft: theme.spacing(2)
  }),
  moreButton: {
    marginLeft: 'auto',
    color: colors.blueGrey[200]
  }
};

const normalizeSearchText = (value = '') => value
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ı/g, 'i')
  .trim();

const filterMenuItems = (items, query) => items.reduce((filteredItems, item) => {
  const titleMatches = normalizeSearchText(item.title).includes(query);

  if (!item.items) {
    return titleMatches ? [...filteredItems, item] : filteredItems;
  }

  const matchingChildren = titleMatches
    ? item.items
    : filterMenuItems(item.items, query);

  if (titleMatches || matchingChildren.length > 0) {
    filteredItems.push({
      ...item,
      items: matchingChildren
    });
  }

  return filteredItems;
}, []);

const countMenuDestinations = (items) => items.reduce((total, item) => (
  total + (item.items ? countMenuDestinations(item.items) : 1)
), 0);

function renderNavItems({
  // eslint-disable-next-line react/prop-types
  items, key, searching, ...rest
}, klima_teknisyeni) {
  return (
    <List key={key} disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, searching, ...rest }, klima_teknisyeni),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc, pathname, item, depth = 0, searching
}, klima_teknisyeni) {
  if (klima_teknisyeni && item.klima_teknisyeni_gizle && item.klima_teknisyeni_gizle) {
    return acc;
  }
  if (item.items) {
    const open = item.href
      ? matchPath({ path: item.href, end: false }, pathname)
      : false;

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={item.href || item.title}
        label={item.label}
        open={Boolean(open) || searching}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          searching
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={item.href || item.title}
        label={item.label}
        title={item.title}
      />
    );
  }

  return acc;
}
let klima_teknisyeni = false;
function NavBar({
  open,
  onClose,
  className,
  ...rest
}) {
  const Menu = getMenu();
  const MenuCount = Menu.length;
  const location = useLocation();
  const [navConfig, setnavConfig] = React.useState([
    {
      subheader: 'İşlemler',
      items: Menu
    }
  ]
  );
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    setnavConfig([
      {
        subheader: 'İşlemler',
        items: Menu
      }
    ]);
    // eslint-disable-next-line
  }, [location.pathname, MenuCount, sessionStorage.getItem('MenuData'), localStorage.getItem('MenuData')]);

  const normalizedQuery = normalizeSearchText(searchQuery);
  const filteredNavConfig = useMemo(() => navConfig.map((list) => ({
    ...list,
    items: normalizedQuery ? filterMenuItems(list.items, normalizedQuery) : list.items
  })), [navConfig, normalizedQuery]);
  const resultCount = useMemo(() => filteredNavConfig.reduce(
    (total, list) => total + countMenuDestinations(list.items),
    0
  ), [filteredNavConfig]);


  const content = (
    <div
      {...rest}
      className={className}
      style={styles.root}
    >
      <Box sx={styles.menuHeader}>
        <Box sx={styles.menuTitleRow}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} lineHeight={1.5}>
            İşlemler
          </Typography>
          {normalizedQuery && (
            <Typography variant="caption" color="text.secondary">
              {resultCount} sonuç
            </Typography>
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setSearchQuery('');
              event.currentTarget.blur();
            }
          }}
          placeholder="Menüde ara..."
          inputProps={{ 'aria-label': 'Menüde ara' }}
          sx={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Aramayı temizle"
                  edge="end"
                  size="small"
                  onClick={() => setSearchQuery('')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      </Box>
      <Box component="nav" sx={styles.navigation}>
        {filteredNavConfig.map((list) => renderNavItems({
          items: list.items,
          pathname: location.pathname,
          key: list.subheader,
          searching: Boolean(normalizedQuery)
        }, klima_teknisyeni))}
        {normalizedQuery && resultCount === 0 && (
          <Box sx={styles.emptyState} role="status">
            <SearchIcon sx={{ fontSize: 30, marginBottom: 0.75, opacity: 0.55 }} />
            <Typography variant="body2" fontWeight={600} color="text.primary">
              Sonuç bulunamadı
            </Typography>
            <Typography variant="caption">
              Farklı bir menü adı deneyin.
            </Typography>
          </Box>
        )}
      </Box>
      <Divider sx={styles.divider} />
    </div>
  );

  return (
    <>
      <Drawer
        anchor="left"
        sx={{ display: { xs: 'block', lg: 'none' } }}
        PaperProps={{ sx: styles.mobileDrawer }}
        onClose={onClose}
        open={open}
        variant="temporary"
      >
        {content}
      </Drawer>
      <Drawer
        anchor="left"
        sx={{ display: { xs: 'none', lg: 'block' } }}
        PaperProps={{ sx: styles.desktopDrawer }}
        open={open}
        onClose={onClose}
        variant="persistent"
      >
        {content}
      </Drawer>
    </>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

export default NavBar;
